import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Select, Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { lendingAPIPutWithdrawalFee } from 'lendingApi/lendingApi';
import {
  useGetAllowedWithdrawalFee,
  useGetWithdrawalFeeRate,
  useGetExpectedCommissions,
} from 'lendingApi/lendingApiHooks';
import _ from 'lodash';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './SummaryCard.module.css';

export type TWithdrawalFeeOfferData = Partial<
  TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number]
> & {
  offer_id: string;
  product_type: any;
  withdrawal_fee_percentage: number;
  adjustable_fee: boolean;
};

type TWithdrawalFeeProps = {
  isDisabled: boolean;
  offer: TWithdrawalFeeOfferData;
  applicationId: string;
  withdrawalFee: number;
  offerIsSelected: boolean;
  offerNotSelected: boolean;
  showExpectedCommissions: boolean;
  productName?: string;
  setIsLoadingPayments: Dispatch<SetStateAction<boolean>>;
  setWithdrawalFee: Dispatch<SetStateAction<number>>;
  isPartnerChannel: boolean;
};

export const WithdrawalFee = ({
  isDisabled,
  offer,
  applicationId,
  withdrawalFee,
  offerIsSelected,
  offerNotSelected,
  showExpectedCommissions,
  setIsLoadingPayments,
  setWithdrawalFee,
  isPartnerChannel,
}: TWithdrawalFeeProps) => {
  const { isDE } = useLocale();
  const { trackEvent } = useAmplitude();
  const { formatPercentage } = useNumberFormat();
  const { introducerId } = useIntroducerContext();

  const { refetch: refetchWithdrawalFeeRate } = useGetWithdrawalFeeRate(
    offer.offer_id,
  );

  const { refetch: refetchExpectedCommission } = useGetExpectedCommissions(
    introducerId,
    applicationId,
    offer.offer_id,
    showExpectedCommissions,
  );

  const { data: allowedWithdrawalFees, isLoading: withdrawalFeeLoading } =
    useGetAllowedWithdrawalFee(
      introducerId as string,
      applicationId,
      offer.offer_id,
      offer.product_type,
    );

  if (!offer.adjustable_fee || isDisabled || isPartnerChannel || isDE) {
    return (
      <p
        className={clsx(styles.value, {
          [styles.selectedValue]: offerIsSelected,
          [styles.unselectedValue]: offerNotSelected,
        })}
      >
        {formatPercentage(offer.withdrawal_fee_percentage)}
      </p>
    );
  }

  if (withdrawalFeeLoading) {
    return (
      <div className={styles.loader}>
        <Spinner />
      </div>
    );
  }

  const handleCommissionSelect = async (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const element = event.target as HTMLSelectElement;
    const feeRate = _.toNumber(element.value);
    const offerId = offer.offer_id;

    trackEvent(AMPLITUDE_EVENTS.commissionFeeClicked, {
      'Withdrawal fee rate': feeRate,
    });

    await lendingAPIPutWithdrawalFee({
      introducerId: introducerId as string,
      applicationId,
      offerId,
      feeRate,
      setLoading: setIsLoadingPayments,
      // TODO need to discuss error states
      onError: () => console.log('error'),
      onSuccess: () => {
        setWithdrawalFee(feeRate);
      },
    });
    refetchWithdrawalFeeRate();
    refetchExpectedCommission();
  };

  return (
    <div
      className={clsx(styles.selectContainer, {
        [styles.selectedValue]: offerIsSelected,
        [styles.unselectedValue]: offerNotSelected,
      })}
    >
      <Select
        className={styles.dropdown}
        data-testid="commission-select"
        onChange={(e) => handleCommissionSelect(e)}
        value={withdrawalFee}
      >
        <option disabled>Choose an option</option>
        {allowedWithdrawalFees?.data?.allowed_fee_rates.map(
          ({ fee_rate, average_monthly_repayment, allowed }) => {
            if ((!fee_rate && fee_rate !== 0) || !average_monthly_repayment) {
              return null;
            }

            const feePercentage = formatPercentage(fee_rate);

            return (
              <option key={`${fee_rate}`} disabled={!allowed} value={fee_rate}>
                {feePercentage}
              </option>
            );
          },
        )}
      </Select>
    </div>
  );
};
